import type { PropsWithChildren } from 'react';
import clsx from 'clsx';
import { LoadingTextSuspense } from 'components/LoadingTextSuspense/LoadingTextSuspense';
import React, { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Link } from 'wouter';

import { Badge } from '@fragment/ui/src/components/Badge/Badge';
import { Icon } from '@fragment/ui/src/components/Icon';
import { ScrollingOverflowText } from '@fragment/ui/src/components/ScrollingOverflowText';
import { POSTHOG_MASK_CLASS } from '@fragment/ui/src/constants';
import { useInlineCopy } from '@fragment/ui/src/hooks/useInlineCopy';

const DetailPaneErrorFallback = ({ error }: { error: Error }) => (
  <div className="flex flex-col space-y-f2">
    <Badge badge={<Icon type="error" backgroundColor="orange" />}>
      Something went wrong
    </Badge>
    <span>{error.message}</span>
  </div>
);

const DetailPaneErrorBoundary = ({ children }: PropsWithChildren) => {
  const onError = useCallback((error: Error) => {
    console.error(error);
  }, []);
  return (
    <ErrorBoundary
      onError={onError}
      FallbackComponent={DetailPaneErrorFallback}
    >
      {children}
    </ErrorBoundary>
  );
};

const DetailPaneSectionErrorFallback = ({ error }: { error: Error }) => (
  <div className="flex flex-col">
    <Badge badge={<Icon type="error" backgroundColor="orange" />}>
      Something went wrong
    </Badge>
    <span>{error.message}</span>
  </div>
);

const DetailPaneSectionErrorBoundary = ({ children }: PropsWithChildren) => {
  const onError = useCallback((error: Error) => {
    console.error(error);
  }, []);
  return (
    <ErrorBoundary
      onError={onError}
      FallbackComponent={DetailPaneSectionErrorFallback}
    >
      {children}
    </ErrorBoundary>
  );
};

type ContainerProps = PropsWithChildren<{
  loadingText: string;
}>;

const Container = ({ loadingText, children }: ContainerProps) => (
  <div
    data-testid="detail-pane"
    className={clsx(
      'flex flex-col',
      'space-y-f2',
      'min-w-f0 w-full h-fit max-h-full overflow-y-scroll scrollbar-hide'
    )}
  >
    <DetailPaneErrorBoundary>
      <LoadingTextSuspense text={loadingText}>{children}</LoadingTextSuspense>
    </DetailPaneErrorBoundary>
  </div>
);

const Section = ({
  loadingText = 'Loading',
  children,
}: PropsWithChildren<{ loadingText?: string }>) => (
  <div
    className={clsx(
      'flex flex-col space-y-f2',
      'border-t-[0.5px] first:border-t-[0px]',
      'border-main-300',
      'py-f2 first:pt-f0'
    )}
  >
    <DetailPaneSectionErrorBoundary>
      <LoadingTextSuspense text={loadingText}>{children}</LoadingTextSuspense>
    </DetailPaneSectionErrorBoundary>
  </div>
);

const V2ItemBodyWithLink = ({
  children,
  linkHref,
}: PropsWithChildren<{ linkHref: string }>) => (
  <Link
    className={clsx(
      POSTHOG_MASK_CLASS,
      'flex w-full',
      'group hover:cursor-pointer hover:bg-negative',
      'truncate'
    )}
    href={linkHref}
  >
    {children}
    <span className="shrink-0 hidden group-hover:block">
      <Icon type="right" size="md" />
    </span>
  </Link>
);

const V2ItemBodyWithCopy = ({
  children,
  textToCopy,
}: PropsWithChildren<{ textToCopy: string }>) => {
  const { handler, isCopied } = useInlineCopy({
    textToCopy,
    onCopyError: () => {},
  });
  return (
    <button
      className="flex flex-row justify-between text-left group min-w-f0 w-full hover:bg-negative hover:cursor-pointer"
      onClick={handler}
      type="button"
    >
      <div className={clsx(POSTHOG_MASK_CLASS, 'grow text-main truncate')}>
        {children}
      </div>
      <div className="hidden group-hover:block w-[6ch] text-right text-main-500">
        {isCopied ? 'Copied' : 'Copy'}
      </div>
    </button>
  );
};

const V2ItemBodyDefault = ({ children }: PropsWithChildren) => (
  <div className={clsx(POSTHOG_MASK_CLASS, 'grow text-main truncate')}>
    {children}
  </div>
);

const V2Item = ({
  label,
  textToCopy,
  children,
  linkHref,
  maskLabel = false,
}: PropsWithChildren<{
  label: string;
  textToCopy?: string | null;
  linkHref?: string | null;
  // Set when the label itself is ledger data (e.g. a tag key), not a static
  // field name — the value body is always masked.
  maskLabel?: boolean;
}>) => {
  if (textToCopy && linkHref) {
    throw new Error('Cannot have both textToCopy and linkHref');
  }

  return (
    <div className="flex flex-col">
      <div
        className={clsx(
          'w-full shrink-0 text-main-500',
          maskLabel && POSTHOG_MASK_CLASS
        )}
      >
        {label}
      </div>
      <div className="flex flex-row justify-between group min-w-f0 w-full border-main-300 border-[0.5px]">
        {linkHref && (
          <V2ItemBodyWithLink linkHref={linkHref}>
            {children}
          </V2ItemBodyWithLink>
        )}
        {textToCopy && (
          <V2ItemBodyWithCopy textToCopy={textToCopy}>
            {children}
          </V2ItemBodyWithCopy>
        )}
        {!linkHref && !textToCopy && (
          <V2ItemBodyDefault>{children}</V2ItemBodyDefault>
        )}
      </div>
    </div>
  );
};

/**
 * A row of a key/value map, where the key is data rather than a field name.
 *
 * Both halves are masked, unlike `V2Item`, whose label defaults to visible.
 */
const KVRow = ({ itemKey, value }: { itemKey: string; value: string }) => (
  <div className="flex flex-row border-[0.5px] border-main-300 border-b-0 last:border-b-[0.5px]">
    <div className="w-1/2 shrink-0 border-r-[0.5px] border-main-300">
      <ScrollingOverflowText
        text={itemKey}
        textClassName={clsx(POSTHOG_MASK_CLASS, 'text-main-500')}
      />
    </div>
    <V2ItemBodyWithCopy textToCopy={value}>{value}</V2ItemBodyWithCopy>
  </div>
);

export const DetailPane = {
  Container,
  Section,
  V2Item,
  KVRow,
};
